import { CloseIcon, CollapseIcon, ExpandIcon } from './assets/icons'
import {
  CLOSE_ANIMATION_TIMEOUT_MS,
  CLOSE_ACTION_NAME,
  CLOSE_CLASS,
  CLOSE_TEST_ID,
  CLOSING_ATTRIBUTE,
  EXPAND_ACTION_NAME,
  EXPAND_CLASS,
  EXPAND_TEST_ID,
  EXPANDED_ATTRIBUTE,
  FRAME_CLASS,
  FRAME_SELECTOR,
  LOAD_REQUESTED_ATTRIBUTE,
  PANEL_CLASS,
  PANEL_SELECTOR,
} from './customer-agent.consts'
import type { IPanelButton } from './customer-agent.types'

import './customer-agent.styles.css'

const findFrame = (): HTMLElement | null => document.querySelector(FRAME_SELECTOR)

export const isChatInThePanel = (): boolean => !!document.querySelector(PANEL_SELECTOR)?.hasChildNodes()

const isPanelOnThePage = (): boolean => !!findFrame()

export const hasLoadBeenRequested = (): boolean => !!findFrame()?.hasAttribute(LOAD_REQUESTED_ATTRIBUTE)

export const markLoadRequested = (): void => findFrame()?.setAttribute(LOAD_REQUESTED_ATTRIBUTE, '')

const buildPanelButton = ({ className, icon, label, testId, actionName, onClick }: IPanelButton): HTMLButtonElement => {
  const button = document.createElement('button')
  button.className = className
  button.type = 'button'
  button.append(icon)
  button.setAttribute('aria-label', label)
  button.dataset.testid = testId
  button.dataset.ddActionName = actionName
  button.addEventListener('click', onClick)
  return button
}

// The inline embed drops the expand control the floating widget has, and HubSpot's iframe reflows to
// whatever size it is given.
const toggleExpanded = (frame: HTMLElement, expandButton: HTMLButtonElement): void => {
  const isExpanded = frame.toggleAttribute(EXPANDED_ATTRIBUTE)
  expandButton.replaceChildren(isExpanded ? CollapseIcon() : ExpandIcon())
  expandButton.setAttribute('aria-label', isExpanded ? 'Collapse chat' : 'Expand chat')
  expandButton.setAttribute('aria-pressed', String(isExpanded))
}

// Reopening mid-animation cancels the close: the chat is still loaded at that point, so calling off the
// removal the animation was leading to is all there is to do.
export const cancelClosingAnimation = (): void => findFrame()?.removeAttribute(CLOSING_ATTRIBUTE)

// HubSpot's `remove()` is synchronous, so the animation has to finish before it runs. The timeout covers
// a page that never received the stylesheet, where `animationend` would never fire.
export const playClosingAnimation = (onFinish: () => void): void => {
  const frame = findFrame()
  if (!frame || !isChatInThePanel()) {
    onFinish()
    return
  }

  // A second close during the animation would stack another listener and another timeout on the frame.
  if (frame.hasAttribute(CLOSING_ATTRIBUTE)) return

  let hasFinished = false
  const finish = (): void => {
    if (hasFinished) return
    hasFinished = true
    // Gone means a reopen cancelled the close, and the removal it was leading to no longer applies.
    if (!frame.hasAttribute(CLOSING_ATTRIBUTE)) return
    frame.removeAttribute(CLOSING_ATTRIBUTE)
    onFinish()
  }

  frame.addEventListener('animationend', finish, { once: true })
  window.setTimeout(finish, CLOSE_ANIMATION_TIMEOUT_MS)
  frame.setAttribute(CLOSING_ATTRIBUTE, '')
}

// The panel stays on the page after a close: a `load()` with its target missing wedges the widget until a
// page reload. The buttons are siblings of it, never children, because HubSpot empties it on `remove()`.
export const mountPanel = (onClose: () => void): void => {
  if (isPanelOnThePage()) return

  const panel = document.createElement('div')
  panel.className = PANEL_CLASS

  const frame = document.createElement('div')
  frame.className = FRAME_CLASS

  const expandButton = buildPanelButton({
    className: EXPAND_CLASS,
    icon: ExpandIcon(),
    label: 'Expand chat',
    testId: EXPAND_TEST_ID,
    actionName: EXPAND_ACTION_NAME,
    onClick: () => toggleExpanded(frame, expandButton),
  })
  expandButton.setAttribute('aria-pressed', 'false')

  const closeButton = buildPanelButton({
    className: CLOSE_CLASS,
    icon: CloseIcon(),
    label: 'Close chat',
    testId: CLOSE_TEST_ID,
    actionName: CLOSE_ACTION_NAME,
    onClick: onClose,
  })

  frame.append(panel, expandButton, closeButton)

  document.body.append(frame)
}
